import { ArrowLeftIcon, ArrowRightIcon, ButtonIcon, css, styled } from 'canoe';
import React, { useEffect, useMemo, useRef } from 'react';

import { useFloatingMenuNavigation } from 'craft/containers/FloatingMenu/commons/useFloatingMenuNavigation';

const TabsWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-left: 7px;
  overflow-y: visible;
  overflow-x: hidden;
`;

const TabsNav = styled.div<{ showArrows: boolean }>`
  ${({ theme, showArrows }) => css`
    height: 100%;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 10px;
    flex-shrink: 0;
    position: relative;

    ${showArrows &&
    css`
      padding: 0 ${theme.spaces.l} 0 calc(${theme.spaces.s} - 7px);
    `}
  `}
`;

const NavArrow = styled.div<{ direction: 'left' | 'right' }>`
  ${({ direction }) => css`
    position: absolute;

    ${direction === 'left' &&
    css`
      left: -16px;
    `}
    ${direction === 'right' &&
    css`
      right: 0;
    `}
  `}
`;

interface Props {
  children: React.ReactNode;
  activeTabIndex: number | undefined;
  tabIds: string[];
  setVisibleTabs: (tabs: string[]) => void;
}

export const FloatingMenuNav = React.memo(
  ({ children, activeTabIndex, tabIds, setVisibleTabs }: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLDivElement>(null);

    const memoizedTabIds = useMemo(() => tabIds, [tabIds]);
    const memoizedActiveTabIndex = useMemo(() => activeTabIndex, [activeTabIndex]);

    const { visibleTabs, arrowsState, navigateLeft, navigateRight } = useFloatingMenuNavigation({
      tabs: memoizedTabIds,
      wrapperRef,
      activeTabIndex: memoizedActiveTabIndex,
    });

    const memoizedChildren = useMemo(() => children, [children]);

    useEffect(() => {
      setVisibleTabs?.(visibleTabs);
    }, [visibleTabs, setVisibleTabs]);

    return (
      <TabsNav ref={navRef} showArrows={arrowsState.shouldShowArrows}>
        {arrowsState.shouldShowArrows && (
          <NavArrow direction="left">
            <ButtonIcon
              icon={ArrowLeftIcon}
              color="white"
              onClick={() => navigateLeft()}
              size="small"
              disabled={!arrowsState.isLeftArrowActive}
            />
          </NavArrow>
        )}

        <TabsWrapper id="floating-menu-tabs-wrapper" ref={wrapperRef}>
          {memoizedChildren}
        </TabsWrapper>

        {arrowsState.shouldShowArrows && (
          <NavArrow direction="right">
            <ButtonIcon
              icon={ArrowRightIcon}
              color="white"
              onClick={() => navigateRight()}
              size="small"
              disabled={!arrowsState.isRightArrowActive}
            />
          </NavArrow>
        )}
      </TabsNav>
    );
  },
);
